import { useSuspenseQuery } from '@tanstack/react-query';

import { getRequest } from '@/api/rest/hooks/requests/get';
import {
  GET_INDICATOR_PRODUCTS_PATH,
  GetIndicatorProductsErrorDto,
  GetIndicatorProductsRequestQueryParams,
  GetIndicatorProductsSuccessDto,
} from '@/api/rest/resources/types/IndicatorProduct';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { assertIsDefined } from '@/utils/assert';

type SuccessDto = GetIndicatorProductsSuccessDto;
type ErrorDto = GetIndicatorProductsErrorDto;

/**
 * @param filters - Optional filters to apply when fetching Indicator Products. If `organization_id` is not provided, it will be obtained from the current context using `useOrganizationId()`.
 *
 * @returns A query result containing the list of Indicator Products.
 */
export const useIndicatorProduct = (filters?: Partial<GetIndicatorProductsRequestQueryParams>) => {
  const orgId = useOrganizationId();

  const organizationId = filters?.organization_id ?? orgId;

  assertIsDefined(organizationId, 'organizationId');

  const queryParams = { organization_id: organizationId, ...filters };

  const queryFn = async () => {
    return getRequest<SuccessDto>(GET_INDICATOR_PRODUCTS_PATH, undefined, queryParams);
  };

  return useSuspenseQuery<SuccessDto, ErrorDto>({
    queryKey: ['plots', 'organization', 'indicator-products', queryParams],
    queryFn,
    staleTime: 1000 * 60 * 5,
  });
};
